// Quick Filters: Figma Pattern 180, ONE shared chip row (Blank PATTERNS.md BP-84; Aaron's ruling
// 2026-10-13, "Figma Pattern 180").
//
// The row under a list grid's toolbar (the Tools list, Inventory Demand: the grid adapter
// datagrid/StatusQuickFilters.tsx) and under a board (the Flights / Bookings calendars). Before it
// there were three chip looks: Demand's StatusQuickFilterBar (24px pills, 10px capitals), this
// file's copy of it for the boards, and PR #45's Tools chips (ModuleListQuickFilters). All three
// draw from here now.
//
// Pattern 180's chip: 24px high, 4px corner, 13px sentence case (labels as the tenant serves
// them). Unselected grey; selected the THEME primary (the tenant's accent, MUI `color="primary"`,
// as Demand's always was), never an --an-teal-* literal. "All" is the empty selection.
//
// @figma Pattern 180 · Quick Filters
// https://www.figma.com/design/HDBjSeAYxCpDOHrBaib9ik/Tools?node-id=53-2524
// (geometry: PR #45's verified Tools build). Its visibility switch is Component 73's Show Quick
// Filters (datagrid/filterPanel.tsx) on a grid, BoardFiltersPopover's on a board.
import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Popover,
  Switch,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import CloseIcon from "@mui/icons-material/Close";

import { DATA_GRID_HEADER_KEYLINE } from "../utils/dataGridSx";

export interface QuickFilterOption {
  value: string;
  label: string;
}

/** Pattern 180's chip: fixed height, width follows the label. */
const CHIP_HEIGHT = 24;

export const quickFilterChipSx = {
  height: CHIP_HEIGHT,
  minHeight: CHIP_HEIGHT,
  borderRadius: "4px",
  boxSizing: "border-box" as const,
  flex: "0 0 auto",
  fontFamily: '"Roboto-Medium", "Roboto", sans-serif',
  fontWeight: 400,
  fontSize: "13px",
  letterSpacing: "0.06em",
  textTransform: "none" as const,
  "& .MuiChip-label": { px: "14px", py: 0, whiteSpace: "nowrap", lineHeight: `${CHIP_HEIGHT}px` },
};

export const unselectedQuickFilterChipSx = {
  ...quickFilterChipSx,
  backgroundColor: grey[200],
  color: grey[700],
  "&:hover": { backgroundColor: grey[300] },
};

/** Pattern 180's row: under the toolbar, across the grid's gutters, above a keyline. */
export const quickFilterRowSx = {
  mx: { xs: 1, md: "50px" },
  borderTop: DATA_GRID_HEADER_KEYLINE,
  py: 1.5,
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap" as const,
  gap: 3,
};

const quickFilterLabelSx = { fontSize: "16px", fontWeight: 500, lineHeight: "24px", whiteSpace: "nowrap" as const };

/**
 * A chip click. `multiple`: the chip adds or removes itself (Demand's toggle); otherwise it is the
 * one selection, a second click clearing it (Tools). An empty selection is All. `exhaustive` (a
 * board, whose options are every kind there is): selecting every option IS All, so it collapses
 * back to the empty selection. A grid's status codes are not exhaustive (Demand has Blank).
 */
export function nextQuickFilterSelection(
  selected: string[],
  value: string,
  options: QuickFilterOption[],
  { multiple = true, exhaustive = true }: { multiple?: boolean; exhaustive?: boolean } = {}
): string[] {
  if (!multiple) return selected.length === 1 && selected[0] === value ? [] : [value];
  const next = selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value];
  return exhaustive && options.every((o) => next.includes(o.value)) ? [] : next;
}

export interface QuickFilterChipsProps {
  options: QuickFilterOption[];
  /** Empty = All. */
  selected: string[];
  onChange: (next: string[]) => void;
  /** Several at once (default) or one (a second click clears it). */
  multiple?: boolean;
  /** Every option selected collapses to All (default: a board's options are exhaustive). */
  exhaustive?: boolean;
  /**
   * Whether All reads selected; default: the selection is empty. For a row whose empty
   * selection can also mean "none" until the viewer picks (the Log Book entry's Generate
   * Lines fields, when the tenant starts them unticked): All then reads unselected.
   */
  allSelected?: boolean;
  /** No chip takes a click (MUI Chip `disabled`): a read-only record, or while it works. */
  disabled?: boolean;
}

export function QuickFilterChips({
  options,
  selected,
  onChange,
  multiple = true,
  exhaustive = true,
  allSelected: allSelectedProp,
  disabled,
}: QuickFilterChipsProps) {
  const allSelected = allSelectedProp ?? selected.length === 0;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
      <Chip
        clickable
        disabled={disabled}
        size="small"
        color={allSelected ? "primary" : "default"}
        label="All"
        onClick={disabled ? undefined : () => onChange([])}
        data-testid="quick-filter-all"
        aria-pressed={allSelected}
        sx={allSelected ? quickFilterChipSx : unselectedQuickFilterChipSx}
      />
      {options.map((opt) => {
        const isSelected = selected.includes(opt.value);
        return (
          <Chip
            key={opt.value}
            clickable
            disabled={disabled}
            size="small"
            color={isSelected ? "primary" : "default"}
            label={opt.label}
            onClick={
              disabled
                ? undefined
                : () => onChange(nextQuickFilterSelection(selected, opt.value, options, { multiple, exhaustive }))
            }
            data-testid={`quick-filter-${opt.value}`}
            aria-pressed={isSelected}
            sx={isSelected ? quickFilterChipSx : unselectedQuickFilterChipSx}
          />
        );
      })}
    </Box>
  );
}

/** "Quick Filters" and its chips, for a row that holds other controls too (Demand's Group By). */
export function QuickFilterGroup(props: QuickFilterChipsProps) {
  return (
    <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 3 }}>
      <Typography sx={quickFilterLabelSx}>Quick Filters</Typography>
      <QuickFilterChips {...props} />
    </Box>
  );
}

/**
 * A NAMED chip set among a row's other controls: its label, then the chips (All + the
 * options), sized to its content. The Log Book entry's Generate Lines Task Type / Task Status
 * (Aaron 2026-10-20, "type and status should be able to be multi select and clickable"),
 * beside its field chips.
 */
export function QuickFilterField({ label, testId, ...chips }: QuickFilterChipsProps & { label: string; testId?: string }) {
  return (
    <Box role="group" aria-label={label} data-testid={testId} sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
      <Typography sx={quickFilterLabelSx}>{label}</Typography>
      <QuickFilterChips {...chips} />
    </Box>
  );
}

export interface QuickFilterBarProps extends QuickFilterChipsProps {
  testId?: string;
  /** Hides the row; the Show Quick Filters switch in FILTERS brings it back. */
  onClose?: () => void;
}

/** The row under a toolbar (Pattern 180). */
export function QuickFilterBar({ testId, onClose, ...chips }: QuickFilterBarProps) {
  return (
    <Box data-testid={testId} sx={quickFilterRowSx}>
      <QuickFilterGroup {...chips} />
      {onClose ? (
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Close quick filters bar"
          title="Hide quick filters"
          sx={{
            p: 0.5,
            color: grey[500],
            "&:hover": { color: grey[800], backgroundColor: grey[100] },
          }}
        >
          <CloseIcon sx={{ fontSize: "16px" }} />
        </IconButton>
      ) : null}
    </Box>
  );
}

/** The localStorage key of a grid's Show Quick Filters choice. */
export function quickFiltersStorageKey(gridName: string): string {
  return `aeronet-quick-filters:${gridName}`;
}

// Every mounted hook with the same key, so the switch in a grid's Filters panel and the row under
// its toolbar (two separate grid slots) move together.
const quickFilterListeners = new Map<string, Set<(show: boolean) => void>>();

function readShown(storageKey: string, defaultShown: boolean): boolean {
  try {
    const stored = window.localStorage.getItem(storageKey);
    return stored === null ? defaultShown : stored !== "false";
  } catch {
    return defaultShown;
  }
}

/**
 * Whether the Quick Filters row shows, remembered per viewer in this browser (localStorage, every
 * read and write in try/catch: blocked storage keeps the choice for this visit only).
 * `defaultShown` applies until the viewer chooses: a board's row starts shown; a list grid's
 * (Tools, PR #45) starts hidden.
 */
export function useShowQuickFilters(
  storageKey: string,
  defaultShown = true
): [boolean, (show: boolean) => void] {
  const [show, setLocal] = useState<boolean>(() => readShown(storageKey, defaultShown));
  useEffect(() => {
    setLocal(readShown(storageKey, defaultShown));
    const listeners = quickFilterListeners.get(storageKey) ?? new Set();
    listeners.add(setLocal);
    quickFilterListeners.set(storageKey, listeners);
    return () => {
      listeners.delete(setLocal);
      if (!listeners.size) quickFilterListeners.delete(storageKey);
    };
  }, [storageKey, defaultShown]);
  const setShow = useCallback(
    (next: boolean) => {
      try {
        window.localStorage.setItem(storageKey, String(next));
      } catch {
        /* storage blocked: the choice lasts this visit only */
      }
      setLocal(next);
      quickFilterListeners.get(storageKey)?.forEach((listener) => listener(next));
    },
    [storageKey]
  );
  return [show, setShow];
}

export interface BoardFiltersPopoverProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  /** The Show Quick Filters switch. Omit BOTH on a board that has no Quick
   *  Filters row (Flight & Duty, H67): a switch for a row that is not there
   *  would be a present-and-inert control. */
  showQuickFilters?: boolean;
  onShowQuickFiltersChange?: (show: boolean) => void;
  filtersActive: boolean;
  onClearAll: () => void;
  /** The board's own filter fields. */
  children: React.ReactNode;
  /** Keep the fields mounted while closed (MUI Popover `keepMounted`). */
  keepMounted?: boolean;
  /** Width of the fields column; defaults to the shell's 260 minimum. */
  width?: number;
}

/**
 * The FILTERS popover shell for a board: Show Quick Filters on top (Dez's
 * mock), then the board's fields, then Clear all filters.
 */
export function BoardFiltersPopover({
  anchorEl,
  onClose,
  showQuickFilters,
  onShowQuickFiltersChange,
  filtersActive,
  onClearAll,
  children,
  keepMounted,
  width,
}: BoardFiltersPopoverProps) {
  const handleSwitch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onShowQuickFiltersChange?.(e.target.checked),
    [onShowQuickFiltersChange]
  );
  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      keepMounted={keepMounted}
      PaperProps={{
        sx: {
          p: 2,
          minWidth: 280,
          borderRadius: "4px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
        },
      }}
    >
      <Box sx={{ minWidth: 260, width }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1.5,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "14px", color: "#111" }}>
            Filters
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close filters"
            sx={{ p: 0.25, color: grey[500], "&:hover": { color: grey[800] } }}
          >
            <CloseIcon sx={{ fontSize: "18px" }} />
          </IconButton>
        </Box>

        {onShowQuickFiltersChange && (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                py: 0.5,
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: "13px", color: "#111", pr: 2 }}>
                Show Quick Filters
              </Typography>
              <Switch
                size="small"
                checked={showQuickFilters ?? true}
                onChange={handleSwitch}
                color="primary"
                inputProps={{ "aria-label": "Show Quick Filters" }}
              />
            </Box>

            <Divider sx={{ my: 1.5 }} />
          </>
        )}

        {children}

        <Divider sx={{ my: 2 }} />
        <Button
          size="small"
          disabled={!filtersActive}
          onClick={onClearAll}
          sx={{ fontWeight: 600, textTransform: "none", p: 0 }}
        >
          Clear all filters
        </Button>
      </Box>
    </Popover>
  );
}
